/**
 * Reference data and enumerations (docs/05 §2–§3, §8; docs/03 §02–§03; docs/06).
 * The API seed (`apps/api/src/db/seed.ts`) writes these values into the database.
 */
import type {
  ChecklistPhase,
  DocKind,
  DocStatus,
  DocumentKind,
  HHMM,
  InvalidateEntity,
  IsoDate,
  MovementType,
  MovementTypeId,
  PointCode,
  Role,
  Settings,
  SignatureRole,
  TrafficLevel,
} from './types.js';

export const APP_TIME_ZONE = 'Europe/Rome';

// ---------------------------------------------------------------- enumerations

export const ROLES = [
  'admin',
  'supervisor',
  'punto',
  'readonly',
] as const satisfies readonly Role[];

export const ROLE_LABELS: Record<Role, string> = {
  admin: 'Amministratore',
  supervisor: 'Distribution Supervisor',
  punto: 'Responsabile di punto',
  readonly: 'Sola lettura',
};

export const TRAFFIC_LEVELS = [
  'VERDE',
  'ARANCIONE',
  'ROSSO',
] as const satisfies readonly TrafficLevel[];

export interface TrafficLevelInfo {
  label: string;
  /** SOP TPC name. */
  sopName: string;
  description: string;
  /** Strong colour for banners and badges. */
  color: string;
  /** Light background (workbook fills). */
  background: string;
}

export const TRAFFIC_LEVEL_INFO: Record<TrafficLevel, TrafficLevelInfo> = {
  VERDE: {
    label: 'Verde',
    sopName: 'Operatività normale',
    description: 'Operatività normale',
    color: '#2E7D32',
    background: '#C6EFCE',
  },
  ARANCIONE: {
    label: 'Arancione',
    sopName: 'Promo Pause',
    description: 'Pausa temporanea (Promo Pause) su tutti e 4 i punti',
    color: '#E67E22',
    background: '#FFD8A8',
  },
  ROSSO: {
    label: 'Rosso',
    sopName: 'Shut Down',
    description: 'Chiusura definitiva (Shut Down)',
    color: '#C62828',
    background: '#FFC7CE',
  },
};

export const DOC_STATUSES = [
  'bozza',
  'stampato',
  'firmato',
  'archiviato',
] as const satisfies readonly DocStatus[];

export const DOC_STATUS_LABELS: Record<DocStatus, string> = {
  bozza: 'Bozza',
  stampato: 'Stampato',
  firmato: 'Firmato',
  archiviato: 'Archiviato',
};

export const DOC_KINDS = ['DDT', 'VERBALE'] as const satisfies readonly DocKind[];

export const DOCUMENT_KINDS = [
  'pdf',
  'scan',
  'signature',
  'planimetry',
  'other',
] as const satisfies readonly DocumentKind[];

export const SIGNATURE_ROLES = ['sender', 'receiver'] as const satisfies readonly SignatureRole[];

export const CHECKLIST_PHASES = [
  'sera_prima',
  'giorno',
  'ultimo_giorno',
  'primo_giorno',
] as const satisfies readonly ChecklistPhase[];

export const CHECKLIST_PHASE_LABELS: Record<ChecklistPhase, string> = {
  sera_prima: 'Sera prima',
  giorno: 'Giorno',
  ultimo_giorno: 'Ultimo giorno',
  primo_giorno: 'Giorno di apertura',
};

/** Entities used in SSE invalidation events and as the first element of TanStack Query keys. */
export const INVALIDATE_ENTITIES = [
  'settings',
  'points',
  'days',
  'persons',
  'users',
  'movement-types',
  'movements',
  'stock',
  'reports',
  'traffic',
  'closure',
  'planning',
  'checklist',
  'documents',
  'dashboard',
  'audit',
] as const satisfies readonly InvalidateEntity[];

// ---------------------------------------------------------------- movement types

/** Movement type ids by meaning (docs/05 §3). */
export const MOVEMENT_TYPE = {
  PICKUP_FROM_TPC: 1,
  DELIVERY_TO_POINT: 2,
  RETURN_FROM_POINT: 3,
  RETURN_TO_TPC: 4,
  TPC_RECOVERY_FROM_POINT: 5,
  HUB_ADJUSTMENT: 6,
  POINT_ADJUSTMENT: 7,
  DEFECTIVE_RETURN_FROM_POINT: 8,
  DEFECTIVE_FOUND_IN_HUB: 9,
  DEFECTIVE_RETURN_TO_TPC: 10,
} as const satisfies Record<string, MovementTypeId>;

export const MOVEMENT_TYPE_IDS = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10,
] as const satisfies readonly MovementTypeId[];

/** Types whose quantity may be negative (inventory adjustments, sign free). */
export const SIGNED_QUANTITY_TYPE_IDS: readonly MovementTypeId[] = [6, 7];

/** Types printed on a DDT; all the others use a verbale. */
export const DDT_TYPE_IDS: readonly MovementTypeId[] = [1, 4, 10];

/** Placeholder replaced with the point name in `fromLabel` / `toLabel`. */
export const POINT_PLACEHOLDER = '{PUNTO}';

const DDT_TITLE = 'DOCUMENTO DI TRASPORTO (D.d.T.)';

/**
 * The 10 movement types of docs/05 §3 (titles of docs/06 §1–§2). The hub is always named
 * "hub San Romano" (v1.0.2); the v1.0.1 texts ("Hub", the names of the reference workbook) are in
 * `apps/api/src/modules/movement-types/upgrade.ts`, which realigns an existing database and lets
 * the import still recognise the workbook names.
 */
export const MOVEMENT_TYPES: readonly Omit<MovementType, 'createdAt' | 'updatedAt'>[] = [
  {
    id: 1,
    prefix: 'RIT',
    name: 'Ritiro da TPC → hub San Romano',
    hubSign: 1,
    pointSign: 0,
    defectiveSign: 0,
    requiresPoint: false,
    fromLabel: 'Magazzino Palestra Bacchettoni',
    toLabel: 'Hub San Romano',
    docKind: 'DDT',
    docTitle: DDT_TITLE,
    docLabel: 'DDT di ritiro',
    sortOrder: 1,
    active: true,
  },
  {
    id: 2,
    prefix: 'CON',
    name: 'Consegna hub San Romano → Punto',
    hubSign: -1,
    pointSign: 1,
    defectiveSign: 0,
    requiresPoint: true,
    fromLabel: 'Hub San Romano',
    toLabel: POINT_PLACEHOLDER,
    docKind: 'VERBALE',
    docTitle: 'VERBALE DI CONSEGNA E PRESA IN CARICO',
    docLabel: 'Verbale di consegna',
    sortOrder: 2,
    active: true,
  },
  {
    id: 3,
    prefix: 'RIE',
    name: 'Rientro Punto → hub San Romano',
    hubSign: 1,
    pointSign: -1,
    defectiveSign: 0,
    requiresPoint: true,
    fromLabel: POINT_PLACEHOLDER,
    toLabel: 'Hub San Romano',
    docKind: 'VERBALE',
    docTitle: "VERBALE DI RIENTRO CARTE ALL'HUB SAN ROMANO",
    docLabel: 'Verbale di rientro',
    sortOrder: 3,
    active: true,
  },
  {
    id: 4,
    prefix: 'RES',
    name: 'Riconsegna hub San Romano → TPC',
    hubSign: -1,
    pointSign: 0,
    defectiveSign: 0,
    requiresPoint: false,
    fromLabel: 'Hub San Romano',
    toLabel: 'TPC – Magazzino / Pokémon Pavilion',
    docKind: 'DDT',
    docTitle: DDT_TITLE,
    docLabel: 'DDT di riconsegna',
    sortOrder: 4,
    active: true,
  },
  {
    id: 5,
    prefix: 'REC',
    name: 'Recupero TPC dal Punto (Arancione/Rosso)',
    hubSign: 0,
    pointSign: -1,
    defectiveSign: 0,
    requiresPoint: true,
    fromLabel: POINT_PLACEHOLDER,
    toLabel: 'TPC – Pokémon Pavilion',
    docKind: 'VERBALE',
    docTitle: 'VERBALE DI CONSEGNA A TPC (RECUPERO STOCK)',
    docLabel: 'Verbale di recupero',
    sortOrder: 5,
    active: true,
  },
  {
    id: 6,
    prefix: 'RTH',
    name: 'Rettifica inventario hub San Romano (±)',
    hubSign: 1,
    pointSign: 0,
    defectiveSign: 0,
    requiresPoint: false,
    fromLabel: 'Rettifica inventario',
    toLabel: 'Hub San Romano',
    docKind: 'VERBALE',
    docTitle: 'VERBALE DI RETTIFICA INVENTARIO',
    docLabel: 'Verbale di rettifica',
    sortOrder: 6,
    active: true,
  },
  {
    id: 7,
    prefix: 'RTP',
    name: 'Rettifica inventario Punto (±)',
    hubSign: 0,
    pointSign: 1,
    defectiveSign: 0,
    requiresPoint: true,
    fromLabel: 'Rettifica inventario',
    toLabel: POINT_PLACEHOLDER,
    docKind: 'VERBALE',
    docTitle: 'VERBALE DI RETTIFICA INVENTARIO',
    docLabel: 'Verbale di rettifica',
    sortOrder: 7,
    active: true,
  },
  {
    id: 8,
    prefix: 'DIF',
    name: 'Rientro carte difettose Punto → hub San Romano',
    hubSign: 0,
    pointSign: -1,
    defectiveSign: 1,
    requiresPoint: true,
    fromLabel: POINT_PLACEHOLDER,
    toLabel: 'Deposito carte difettose (hub San Romano)',
    docKind: 'VERBALE',
    docTitle: 'VERBALE DI RIENTRO CARTE DIFETTOSE',
    docLabel: 'Verbale rientro carte difettose',
    sortOrder: 8,
    active: true,
  },
  {
    id: 9,
    prefix: 'DFH',
    name: "Carte difettose rilevate all'hub San Romano",
    hubSign: -1,
    pointSign: 0,
    defectiveSign: 1,
    requiresPoint: false,
    fromLabel: 'Hub San Romano',
    toLabel: 'Deposito carte difettose (hub San Romano)',
    docKind: 'VERBALE',
    docTitle: "VERBALE CARTE DIFETTOSE RILEVATE ALL'HUB SAN ROMANO",
    docLabel: 'Verbale carte difettose',
    sortOrder: 9,
    active: true,
  },
  {
    id: 10,
    prefix: 'RDF',
    name: 'Restituzione carte difettose hub San Romano → TPC',
    hubSign: 0,
    pointSign: 0,
    defectiveSign: -1,
    requiresPoint: false,
    fromLabel: 'Deposito carte difettose (hub San Romano)',
    toLabel: 'TPC – Magazzino / Pokémon Pavilion',
    docKind: 'DDT',
    docTitle: DDT_TITLE,
    docLabel: 'DDT restituzione carte difettose',
    sortOrder: 10,
    active: true,
  },
];

// ---------------------------------------------------------------- settings

/** Default settings (docs/05 §2). Addresses and VAT numbers are placeholders to be filled in. */
export const DEFAULT_SETTINGS: Settings = {
  eventName: 'Lucca Comics & Games 2026',
  tpcName: 'The Pokémon Company International',
  tpcAddress: 'da inserire',
  tpcVat: null,
  fmfName: 'FMF S.r.l.',
  fmfAddress: 'da inserire',
  fmfVat: 'da inserire',
  warehouseName: 'Magazzino Palestra Bacchettoni – Lucca',
  hubName: 'Hub FMF – Piazza San Romano, Lucca',
  responsibleName: 'Andrea Vizzari',
  responsiblePhone: '+39 347 519 2946',
  goodsDescription:
    'Carte promozionali Pokémon – Lucca Comics & Games 2026 (omaggio, non destinate alla vendita)',
  reasonPickup: 'Affidamento in custodia per distribuzione gratuita al pubblico',
  reasonReturn: 'Restituzione materiale affidato in custodia',
  reasonDefective:
    'Restituzione carte difettose per verifica qualità – da non distruggere (distruzione a cura di TPC)',
  piecesPerBox: null,
  safetyStockPct: 0.1,
  maxReportIntervalMin: 120,
  readonlyShowsQuantities: false,
  signatureEnabled: true,
};

// ---------------------------------------------------------------- points

export const POINT_CODES = ['SC', 'SR', 'PF', 'BA'] as const satisfies readonly PointCode[];

export interface PointSeed {
  code: PointCode;
  name: string;
  services: string;
  sortOrder: number;
}

/** The four distribution points (docs/03 §02). Seeded in this order: ids 1..4 on an empty DB. */
export const POINTS: readonly PointSeed[] = [
  {
    code: 'SC',
    name: 'Santa Croce',
    services: 'Biglietteria · Welcome Desk · Solo carta',
    sortOrder: 1,
  },
  {
    code: 'SR',
    name: 'San Regolo',
    services: 'Biglietteria · Welcome Desk · Solo carta',
    sortOrder: 2,
  },
  {
    code: 'PF',
    name: 'Polo Fiere',
    services: 'Biglietteria · Welcome Desk · Solo carta',
    sortOrder: 3,
  },
  {
    code: 'BA',
    name: 'Bacchettoni',
    services: 'Solo carta (davanti alla Palestra Bacchettoni)',
    sortOrder: 4,
  },
];

// ---------------------------------------------------------------- operating days and hours

export interface OperatingDaySeed {
  date: IsoDate;
  label: string;
  notes: string | null;
}

/** Operating days 27/10–01/11/2026 (docs/03 §02, docs/05 §2). */
export const OPERATING_DAYS: readonly OperatingDaySeed[] = [
  {
    date: '2026-10-27',
    label: 'Martedì 27/10',
    notes: 'Giorno di apertura: solo Welcome Desk Santa Croce e San Regolo, 16:00–19:00',
  },
  {
    date: '2026-10-28',
    label: 'Mercoledì 28/10',
    notes: 'Ritiro iniziale la sera del 27/10 alle 22:00',
  },
  { date: '2026-10-29', label: 'Giovedì 29/10', notes: null },
  { date: '2026-10-30', label: 'Venerdì 30/10', notes: null },
  { date: '2026-10-31', label: 'Sabato 31/10', notes: null },
  {
    date: '2026-11-01',
    label: 'Domenica 01/11',
    notes: 'Ultimo giorno – Bacchettoni chiude alle 18:30',
  },
];

export interface PointHoursSeed {
  dayDate: IsoDate;
  pointCode: PointCode;
  /** null = closed. */
  openTime: HHMM | null;
  closeTime: HHMM | null;
  note: string | null;
}

function fullDayHours(dayDate: IsoDate, baClose: HHMM): PointHoursSeed[] {
  return [
    { dayDate, pointCode: 'SC', openTime: '06:30', closeTime: '18:30', note: null },
    { dayDate, pointCode: 'SR', openTime: '06:30', closeTime: '18:30', note: null },
    { dayDate, pointCode: 'PF', openTime: '06:30', closeTime: '18:30', note: null },
    { dayDate, pointCode: 'BA', openTime: '06:30', closeTime: baClose, note: null },
  ];
}

/** Opening hours per day and point (docs/03 §02). */
export const POINT_HOURS: readonly PointHoursSeed[] = [
  {
    dayDate: '2026-10-27',
    pointCode: 'SC',
    openTime: '16:00',
    closeTime: '19:00',
    note: 'Solo Welcome Desk',
  },
  {
    dayDate: '2026-10-27',
    pointCode: 'SR',
    openTime: '16:00',
    closeTime: '19:00',
    note: 'Solo Welcome Desk',
  },
  { dayDate: '2026-10-27', pointCode: 'PF', openTime: null, closeTime: null, note: 'Chiuso' },
  { dayDate: '2026-10-27', pointCode: 'BA', openTime: null, closeTime: null, note: 'Chiuso' },
  ...fullDayHours('2026-10-28', '22:00'),
  ...fullDayHours('2026-10-29', '22:00'),
  ...fullDayHours('2026-10-30', '22:00'),
  ...fullDayHours('2026-10-31', '22:00'),
  ...fullDayHours('2026-11-01', '18:30'),
];

// ---------------------------------------------------------------- persons

export type CommandGroup = 'responsabili' | 'direzione';

export interface PersonSeed {
  organization: string;
  name: string;
  roleLabel: string;
  chainOfCommand: boolean;
  /** Group in the chain of command (docs/03 §03); display only, not stored. */
  commandGroup: CommandGroup;
  phone: string;
}

/** Chain of command of docs/03 §03 (the repository is private: phones included by design). */
export const PERSONS: readonly PersonSeed[] = [
  {
    organization: 'FMF S.r.l.',
    name: 'Andrea Vizzari',
    roleLabel: 'Head of Distribution',
    chainOfCommand: true,
    commandGroup: 'responsabili',
    phone: '+39 347 519 2946',
  },
  {
    organization: 'FMF S.r.l.',
    name: 'Luca Trenti',
    roleLabel: 'Distribution Supervisor',
    chainOfCommand: true,
    commandGroup: 'responsabili',
    phone: '+39 345 584 6936',
  },
  {
    organization: 'FMF S.r.l.',
    name: 'Alessandro Broggi',
    roleLabel: 'Distribution Supervisor',
    chainOfCommand: true,
    commandGroup: 'responsabili',
    phone: '+39 351 781 9553',
  },
  {
    organization: 'FMF S.r.l.',
    name: 'Jacopo Musciolà',
    roleLabel: 'Head of FMF',
    chainOfCommand: true,
    commandGroup: 'direzione',
    phone: '+39 340 491 1612',
  },
  {
    organization: 'FMF S.r.l.',
    name: 'Alessandro Baiocchi',
    roleLabel: 'FMF Operation Manager',
    chainOfCommand: true,
    commandGroup: 'direzione',
    phone: '+39 334 790 2071',
  },
  {
    organization: 'FMF S.r.l.',
    name: 'Iustin Ungureanu',
    roleLabel: 'Distribution Manager',
    chainOfCommand: true,
    commandGroup: 'direzione',
    phone: '+39 380 641 4344',
  },
  {
    organization: 'FMF S.r.l.',
    name: 'Sara Vancheri',
    roleLabel: 'Distribution Manager',
    chainOfCommand: true,
    commandGroup: 'direzione',
    phone: '+39 351 787 5260',
  },
];

// ---------------------------------------------------------------- checklist templates

export interface ChecklistTemplateSeed {
  /**
   * Stable identifier of the default template, not stored: the upgrade of an existing database
   * (`apps/api/src/modules/checklist/upgrade.ts`) maps older templates onto it, so texts and
   * time hints can change without losing the link. Never reuse or change a key.
   */
  key: string;
  phase: ChecklistPhase;
  /** `HH:MM`, free text, or `CLOSING_TIME_PLACEHOLDER` (closing time of the point that day). */
  timeHint: string | null;
  section: string;
  text: string;
  responsible: string | null;
  /** Point the item refers to, if any. */
  pointCode: PointCode | null;
}

/**
 * Placeholder of a template time hint replaced, when the items of a day are generated, by the
 * closing time of the template's point that day (18:30, 19:00 on 27/10, Bacchettoni 22:00).
 */
export const CLOSING_TIME_PLACEHOLDER = '{CHIUSURA}';

const REPORT_TIMES = ['08:30', '10:30', '12:30', '14:30', '16:30', '18:30'] as const;

/**
 * Daily checklist templates (docs/05 §8, texts of the workbook "Checklist" sheet adapted to the app).
 *
 * Phases: `sera_prima` = evening before day D (D−1, evening pickup for D); `giorno` = day D;
 * `ultimo_giorno` = only on the last operating day; `primo_giorno` = only on the opening day 27/10,
 * which gets no `sera_prima` and `giorno` items (see `checklistTemplateApplies`). A template bound
 * to a point applies only while the point is open: on the days it is open and, with an `HH:MM`
 * time hint, if it does not close before that time (Bacchettoni evening reports).
 * Items are generated per day with the order of this list (`sortOrder` = index + 1).
 */
export const CHECKLIST_TEMPLATES: readonly ChecklistTemplateSeed[] = [
  // sera prima (D−1): Bacchettoni closes at 22:00, so the pickup follows its return, the hub count
  // and the calculation (docs/07)
  {
    key: 'sera.calcolo',
    phase: 'sera_prima',
    timeHint: 'Dopo il conteggio hub San Romano',
    section: 'Ritiro serale',
    text: 'Calcolo della quantità da ritirare (Pianificazione) in base alla giacenza hub San Romano contata',
    responsible: 'Andrea Vizzari',
    pointCode: null,
  },
  {
    key: 'sera.arrivo',
    phase: 'sera_prima',
    timeHint: '22:00, dopo il rientro di Bacchettoni',
    section: 'Ritiro serale',
    text: 'Arrivo alla Palestra Bacchettoni e conteggio delle carte in contraddittorio con il referente carte TPC',
    responsible: 'FMF + referente TPC',
    pointCode: null,
  },
  {
    key: 'sera.ddt',
    phase: 'sera_prima',
    timeHint: 'Al ritiro',
    section: 'Ritiro serale',
    text: 'DDT di ritiro stampato, compilato e firmato in duplice copia (FMF + TPC)',
    responsible: 'FMF + referente TPC',
    pointCode: null,
  },
  {
    key: 'sera.registrazione',
    phase: 'sera_prima',
    timeHint: 'Dopo il ritiro',
    section: 'Ritiro serale',
    text: 'Ritiro registrato nei Movimenti (quantità, colli, firme)',
    responsible: 'Responsabile distribuzione FMF',
    pointCode: null,
  },
  {
    key: 'sera.trasporto',
    phase: 'sera_prima',
    timeHint: 'Dopo il ritiro',
    section: 'Ritiro serale',
    text: "Trasporto all'hub di Piazza San Romano e custodia presidiata",
    responsible: 'FMF',
    pointCode: null,
  },
  // giorno D
  {
    key: 'giorno.prelievo',
    phase: 'giorno',
    timeHint: '05:30',
    section: 'Rifornimento',
    text: "Prelievo dall'hub San Romano delle quantità assegnate ai 4 punti (Pianificazione)",
    responsible: 'FMF',
    pointCode: null,
  },
  ...POINTS.map((p): ChecklistTemplateSeed => ({
    key: `giorno.consegna.${p.code}`,
    phase: 'giorno',
    timeHint: '06:00',
    section: 'Rifornimento',
    text: `Consegna a ${p.name} con verbale firmato in duplice copia`,
    responsible: 'FMF + resp. punto',
    pointCode: p.code,
  })),
  {
    key: 'giorno.verifica',
    phase: 'giorno',
    timeHint: '06:30',
    section: 'Rifornimento',
    text: 'Verifica: tutti e 4 i punti riforniti entro le 06:30',
    responsible: 'Andrea Vizzari',
    pointCode: null,
  },
  {
    key: 'giorno.briefing',
    phase: 'giorno',
    timeHint: '06:30',
    section: 'Apertura',
    text: 'Briefing staff ai punti: livello semaforo, Q&A approvate, catena di comando, scheda staff consegnata',
    responsible: 'Responsabili punto',
    pointCode: null,
  },
  ...REPORT_TIMES.map((h): ChecklistTemplateSeed => ({
    key: `giorno.report.${h}`,
    phase: 'giorno',
    timeHint: h,
    section: 'Monitoraggio',
    text: `Report delle ${h}: carte distribuite + semaforo dai 4 punti registrati e inviati agli Event Leads TPC`,
    responsible: 'Responsabile distribuzione FMF',
    pointCode: null,
  })),
  // Bacchettoni distributes until 22:00 (Sunday 18:30): only on the days it is still open then
  {
    key: 'giorno.report.BA.20:30',
    phase: 'giorno',
    timeHint: '20:30',
    section: 'Monitoraggio',
    text: 'Report delle 20:30 (Bacchettoni): carte distribuite + semaforo registrati e inviati agli Event Leads TPC',
    responsible: 'Responsabile distribuzione FMF',
    pointCode: 'BA',
  },
  {
    key: 'giorno.report.BA.22:00',
    phase: 'giorno',
    timeHint: '22:00',
    section: 'Monitoraggio',
    text: 'Report di chiusura Bacchettoni delle 22:00: contatore finale + semaforo registrati prima del rientro e inviati agli Event Leads TPC',
    responsible: 'Responsabile distribuzione FMF',
    pointCode: 'BA',
  },
  {
    key: 'giorno.tranche',
    phase: 'giorno',
    timeHint: 'In giornata',
    section: 'Monitoraggio',
    text: 'Eventuali rifornimenti in tranche: verbale di consegna firmato e movimento registrato',
    responsible: 'FMF',
    pointCode: null,
  },
  {
    key: 'giorno.qualita',
    phase: 'giorno',
    timeHint: 'In giornata',
    section: 'Monitoraggio',
    text: "Contestazioni di qualità: carta confrontata con l'immagine di riferimento TPC; se sostituita, difettosa trattenuta in busta dedicata e sostituzione annotata",
    responsible: 'Responsabili punto',
    pointCode: null,
  },
  {
    key: 'giorno.semaforo',
    phase: 'giorno',
    timeHint: 'In giornata',
    section: 'Monitoraggio',
    text: 'Cambi di livello semaforo registrati nel Registro semaforo con le conferme dei 4 punti',
    responsible: 'Andrea Vizzari',
    pointCode: null,
  },
  ...POINTS.filter((p) => p.code !== 'BA').map((p): ChecklistTemplateSeed => ({
    key: `giorno.chiusura.${p.code}`,
    phase: 'giorno',
    timeHint: CLOSING_TIME_PLACEHOLDER,
    section: 'Chiusura',
    text: `Chiusura ${p.name}: conteggio residuo, verbale di rientro firmato, carte all'hub San Romano`,
    responsible: 'FMF + resp. punto',
    pointCode: p.code,
  })),
  {
    key: 'giorno.chiusura.BA',
    phase: 'giorno',
    timeHint: CLOSING_TIME_PLACEHOLDER,
    section: 'Chiusura',
    text: "Chiusura Bacchettoni: conteggio residuo, verbale di rientro firmato, carte all'hub San Romano",
    responsible: 'FMF + resp. punto',
    pointCode: 'BA',
  },
  {
    key: 'giorno.difettose',
    phase: 'giorno',
    timeHint: 'Dopo i rientri',
    section: 'Chiusura',
    text: "Carte difettose rientrate dai punti con verbale dedicato (Rientro carte difettose), custodite all'hub San Romano separate dallo stock",
    responsible: 'Responsabile distribuzione FMF',
    pointCode: null,
  },
  {
    key: 'giorno.conteggio',
    phase: 'giorno',
    timeHint: 'Dopo i rientri',
    section: 'Chiusura',
    text: "Conteggio totale all'hub San Romano e inserimento della giacenza contata (Chiusura)",
    responsible: 'Responsabile distribuzione FMF',
    pointCode: null,
  },
  {
    key: 'giorno.quadratura',
    phase: 'giorno',
    timeHint: 'Dopo i rientri',
    section: 'Chiusura',
    text: 'Quadratura: nessun ⚠ in Chiusura e Movimenti; giacenze comunicate solo agli Event Leads',
    responsible: 'Andrea Vizzari',
    pointCode: null,
  },
  {
    key: 'giorno.archiviazione',
    phase: 'giorno',
    timeHint: 'Fine giornata',
    section: 'Chiusura',
    text: 'Archiviazione degli originali firmati (DDT e verbali) del giorno',
    responsible: 'Responsabile distribuzione FMF',
    pointCode: null,
  },
  // ultimo giorno
  {
    key: 'ultimo.riconsegna',
    phase: 'ultimo_giorno',
    timeHint: 'Fine evento',
    section: 'Chiusura',
    text: 'Riconsegna della giacenza finale a TPC con DDT di riconsegna (se concordato)',
    responsible: 'Andrea Vizzari + TPC',
    pointCode: null,
  },
  {
    key: 'ultimo.difettose',
    phase: 'ultimo_giorno',
    timeHint: 'Fine evento',
    section: 'Chiusura',
    text: 'Restituzione a TPC di tutte le carte difettose con DDT dedicato (Restituzione carte difettose)',
    responsible: 'Andrea Vizzari + TPC',
    pointCode: null,
  },
  // giorno di apertura 27/10: only the Welcome Desks of Santa Croce and San Regolo, 16:00–19:00,
  // supplied by an early pickup (docs/05 §8, docs/07 point 1); the evening pickup of 27/10 belongs
  // to the checklist of 28/10 (sera prima)
  {
    key: 'apertura.ritiro',
    phase: 'primo_giorno',
    timeHint: '15:00',
    section: 'Ritiro anticipato',
    text: "Ritiro anticipato alla Palestra Bacchettoni entro le 15:00 (orario da concordare con TPC): conteggio in contraddittorio con il referente carte TPC, DDT di ritiro firmato in duplice copia (FMF + TPC), ritiro registrato nei Movimenti con giorno operativo forzato al 27/10 (dalle 12:00 l'app lo assegna al 28/10)",
    responsible: 'FMF + referente TPC',
    pointCode: null,
  },
  {
    key: 'apertura.rifornimento',
    phase: 'primo_giorno',
    timeHint: '16:00',
    section: 'Rifornimento',
    text: 'Rifornimento dei Welcome Desk di Santa Croce e San Regolo entro le 16:00, con verbale di consegna firmato in duplice copia',
    responsible: 'Andrea Vizzari',
    pointCode: null,
  },
  {
    key: 'apertura.briefing',
    phase: 'primo_giorno',
    timeHint: '16:00',
    section: 'Apertura',
    text: 'Briefing staff ai Welcome Desk di Santa Croce e San Regolo: livello semaforo, Q&A approvate, catena di comando, scheda staff consegnata',
    responsible: 'Responsabili punto',
    pointCode: null,
  },
  {
    key: 'apertura.report.18:00',
    phase: 'primo_giorno',
    timeHint: '18:00',
    section: 'Monitoraggio',
    text: 'Report delle 18:00: carte distribuite + semaforo dai Welcome Desk di Santa Croce e San Regolo registrati e inviati agli Event Leads TPC',
    responsible: 'Responsabile distribuzione FMF',
    pointCode: null,
  },
  {
    key: 'apertura.report.19:00',
    phase: 'primo_giorno',
    timeHint: '19:00',
    section: 'Monitoraggio',
    text: 'Report di chiusura delle 19:00: contatore finale + semaforo dei due Welcome Desk registrati prima del rientro e inviati agli Event Leads TPC',
    responsible: 'Responsabile distribuzione FMF',
    pointCode: null,
  },
  ...POINTS.filter((p) => p.code === 'SC' || p.code === 'SR').map((p): ChecklistTemplateSeed => ({
    key: `apertura.chiusura.${p.code}`,
    phase: 'primo_giorno',
    timeHint: CLOSING_TIME_PLACEHOLDER,
    section: 'Chiusura',
    text: `Chiusura Welcome Desk ${p.name}: conteggio residuo, verbale di rientro firmato, carte all'hub San Romano`,
    responsible: 'FMF + resp. punto',
    pointCode: p.code,
  })),
  {
    key: 'apertura.conteggio',
    phase: 'primo_giorno',
    timeHint: 'Dopo i rientri',
    section: 'Chiusura',
    text: "Conteggio totale all'hub San Romano dopo il rientro dei Welcome Desk e inserimento della giacenza contata (Chiusura)",
    responsible: 'Responsabile distribuzione FMF',
    pointCode: null,
  },
  {
    key: 'apertura.quadratura',
    phase: 'primo_giorno',
    timeHint: 'Dopo i rientri',
    section: 'Chiusura',
    text: 'Quadratura: nessun ⚠ in Chiusura e Movimenti, ritiro anticipato assegnato al 27/10; giacenze comunicate solo agli Event Leads',
    responsible: 'Andrea Vizzari',
    pointCode: null,
  },
  {
    key: 'apertura.archiviazione',
    phase: 'primo_giorno',
    timeHint: 'Fine giornata',
    section: 'Chiusura',
    text: 'Archiviazione degli originali firmati (DDT e verbali) del giorno',
    responsible: 'Responsabile distribuzione FMF',
    pointCode: null,
  },
];
